/**
 * CrmAssistant — Full-page AI Assistant tab of VD Manager.
 *
 * Design language matched with `AiAssistantDrawer.jsx` (side-drawer version) via the
 * shared `AiUiKit` (Pill, SectionCard, Row, priorityPill, deadlinePill).
 *
 * Backend contract unchanged: reads insights from POST /api/crm/assistant, sends
 * questions with the same session_id for multi-turn, and preserves the advanced
 * settings sub-widgets (briefing toggle, agent-email log, general settings).
 */
import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import LeadDrawer from "./LeadDrawer";
import AiAssistantSettings from "./AiAssistantSettings";
import { Pill, SectionCard, Row, priorityPill, deadlinePill } from "./AiUiKit";
import { Link } from "react-router-dom";
import {
    Sparkles, RefreshCw, Send, Mic, MicOff, Calendar, Clock, ClipboardList,
    User as UserIcon, Home as HomeIcon, CheckCircle2, Flame, PauseCircle,
    Radar, Target, FileWarning, Mail, ChevronDown, ChevronUp,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*                              Main tab component                            */
/* -------------------------------------------------------------------------- */
export default function CrmAssistant() {
    const { isDark } = useTheme();
    const light = !isDark;
    const [data, setData] = useState(null);
    const [plSummary, setPlSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [question, setQuestion] = useState("");
    const [asking, setAsking] = useState(false);
    const [conversation, setConversation] = useState([]);
    const [openLead, setOpenLead] = useState(null);
    const [agents, setAgents] = useState([]);
    const [briefing, setBriefing] = useState(null);
    const [sendingNow, setSendingNow] = useState(false);
    const [listening, setListening] = useState(false);
    const [voiceUnsupported, setVoiceUnsupported] = useState(false);
    const sessionRef = useRef(null);
    const chatEndRef = useRef(null);
    const recognitionRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const [r, a, b, pl] = await Promise.all([
                api.post("/crm/assistant", {}),
                api.get("/crm/agents").catch(() => ({ data: { agents: [] } })),
                api.get("/crm/briefing").catch(() => ({ data: null })),
                api.get("/crm/private-listings/summary").catch(() => ({ data: null })),
            ]);
            setData(r.data);
            sessionRef.current = r.data.session_id;
            setAgents(a.data.agents || []);
            setBriefing(b.data);
            setPlSummary(pl.data);
            if (r.data.ai_text) setConversation([{ role: "ai", text: r.data.ai_text }]);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [conversation, asking]);

    const toggleVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setVoiceUnsupported(true); return; }
        if (listening) {
            try { recognitionRef.current?.stop(); } catch { /* noop */ }
            setListening(false);
            return;
        }
        try {
            const rec = new SR();
            rec.lang = "it-IT";
            rec.interimResults = true;
            rec.continuous = false;
            rec.onresult = (evt) => {
                let transcript = "";
                for (let i = evt.resultIndex; i < evt.results.length; i++) transcript += evt.results[i][0].transcript;
                setQuestion(transcript.trim());
            };
            rec.onerror = () => setListening(false);
            rec.onend = () => setListening(false);
            recognitionRef.current = rec;
            rec.start();
            setListening(true);
        } catch (e) { console.warn("Speech recognition error", e); setListening(false); }
    };

    const ask = async () => {
        const q = question.trim();
        if (!q || asking) return;
        setAsking(true);
        setConversation((c) => [...c, { role: "user", text: q }]);
        setQuestion("");
        try {
            const r = await api.post("/crm/assistant", { question: q, session_id: sessionRef.current });
            sessionRef.current = r.data.session_id;
            setConversation((c) => [...c, {
                role: "ai",
                text: r.data.ai_text || "Non sono riuscito a generare una risposta.",
                drafts: r.data.email_drafts || [],
            }]);
        } catch {
            setConversation((c) => [...c, { role: "ai", text: "Errore temporaneo, riprova." }]);
        } finally { setAsking(false); }
    };

    const toggleBriefing = async () => {
        const next = !briefing?.enabled;
        setBriefing((b) => ({ ...b, enabled: next }));
        try { await api.put("/crm/briefing", { enabled: next }); } catch (e) { console.error(e); }
    };
    const sendBriefingNow = async () => {
        setSendingNow(true);
        try { const r = await api.post("/crm/briefing/send-now"); alert(r.data.detail || "Inviato"); }
        catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setSendingNow(false); }
    };

    if (loading) {
        return (
            <div data-testid="crm-ai-tab" className={`p-12 text-center text-sm ${light ? "text-slate-500" : "text-ink-400"}`}>
                <Sparkles size={28} className={`${light ? "text-[#1E40AF]" : "text-[#5FA7FF]"} mx-auto mb-4 animate-pulse`} />
                L&apos;assistente sta analizzando i tuoi dati CRM…
            </div>
        );
    }

    const ins = data?.insights || {};
    const followUp = ins.da_ricontattare || [];
    const hotLeads = ins.lead_caldi || [];
    const incarichi = ins.incarichi_in_scadenza || [];
    const trattFerme = ins.trattative_ferme || [];
    const matchAlerts = ins.match_alerts || [];
    const docIncomplete = (ins.documentazione && ins.documentazione.immobili_incompleti) || [];
    const senzaAgente = ins.senza_agente || 0;
    const visitePrx = (ins.visite_prossime || []).length;
    const suggestedActions = [
        followUp.length > 0 && `Contatta ${Math.min(3, followUp.length)} lead prioritari`,
        visitePrx > 0 && `Segui ${visitePrx} appuntament${visitePrx === 1 ? "o" : "i"} in programma`,
        incarichi.length > 0 && `Verifica ${incarichi.length} incaric${incarichi.length === 1 ? "o" : "hi"} in scadenza`,
        senzaAgente > 0 && `Assegna ${senzaAgente} lead senza agente`,
    ].filter(Boolean);

    return (
        <div data-testid="crm-ai-tab" className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* -------- Left column: insight cards -------- */}
            <div
                className="lg:col-span-3 space-y-1 rounded-xl"
                style={{ backgroundColor: light ? "#FFFFFF" : "#111111", color: light ? "#0F172A" : "#FFFFFF" }}
            >
                <div className="px-5 pt-5 pb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${light ? "bg-blue-50 border border-blue-200" : "bg-[#0F1E38] border border-[#007BFF]/40"}`}>
                            <Sparkles size={18} className={light ? "text-[#1E40AF]" : "text-[#5FA7FF]"} />
                        </div>
                        <div>
                            <h3 className={`text-[16px] font-semibold leading-tight ${light ? "text-slate-900" : "text-white"}`}>Riepilogo operativo</h3>
                            <p className={`text-[12px] leading-tight mt-0.5 ${light ? "text-slate-500" : "text-ink-400"}`}>Le priorità della giornata basate sui tuoi dati</p>
                        </div>
                    </div>
                    <button
                        data-testid="ai-refresh"
                        onClick={load}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] transition-colors ${light ? "text-slate-700 border border-slate-300 hover:border-slate-500 hover:bg-slate-50" : "text-ink-200 border border-white/15 hover:border-white/40 hover:bg-white/5"}`}
                    >
                        <RefreshCw size={12} /> Aggiorna
                    </button>
                </div>

                <div className="px-5 pb-5">
                    {/* NUOVI ANNUNCI PRIVATI */}
                    {plSummary && plSummary.new_count > 0 && (
                        <SectionCard
                            testid="ai-tab-nuovi-privati"
                            light={light}
                            icon={<Radar size={16} />}
                            iconClass={light ? "text-amber-600" : "text-[#FBBF24]"}
                            title="Nuovi annunci privati"
                            subtitle="Annunci freschi scoperti dai monitoraggi"
                        >
                            <Row
                                testid="ai-tab-pl-row"
                                light={light}
                                icon={<HomeIcon size={16} />}
                                label={`${plSummary.new_count} annunc${plSummary.new_count === 1 ? "io" : "i"} da esaminare`}
                                sub={`+${plSummary.today_count} scoperti oggi · ${plSummary.monitors} monitoraggi attivi`}
                                onClick={() => { window.location.href = "/analytics-pro?tab=nuovi-privati"; }}
                                pills={[{ tone: "amber", label: "Nuovo" }]}
                            />
                        </SectionCard>
                    )}

                    {/* FOLLOW-UP */}
                    <SectionCard
                        testid="ai-ricontattare"
                        light={light}
                        icon={<Calendar size={16} />}
                        title={`Follow-up giornaliero (${followUp.length})`}
                        subtitle="Lead che non rispondono da più tempo"
                    >
                        {followUp.length === 0 && (
                            <div className={`text-[13px] py-3 px-2 ${light ? "text-slate-500" : "text-ink-500"}`}>Ottimo: nessun lead trascurato da più di 7 giorni.</div>
                        )}
                        {followUp.slice(0, 6).map((l) => {
                            const pri = priorityPill(l.priority);
                            return (
                                <Row
                                    key={l.id}
                                    testid={`ai-tab-lead-${l.id}`}
                                    light={light}
                                    icon={<UserIcon size={16} />}
                                    label={l.name}
                                    sub={l.stage ? `Fase: ${l.stage}` : null}
                                    onClick={() => setOpenLead(l.id)}
                                    pills={[
                                        { tone: "red", label: `${l.days_inactive} giorni` },
                                        { tone: pri.tone, label: pri.label },
                                    ]}
                                />
                            );
                        })}
                    </SectionCard>

                    {/* LEAD CALDI */}
                    {hotLeads.length > 0 && (
                        <SectionCard
                            testid="ai-caldi"
                            light={light}
                            icon={<Flame size={16} />}
                            iconClass={light ? "text-amber-600" : "text-[#FBBF24]"}
                            title={`Lead caldi (${hotLeads.length})`}
                            subtitle="Alta priorità o in trattativa attiva"
                        >
                            {hotLeads.slice(0, 6).map((l) => (
                                <Row
                                    key={l.id}
                                    testid={`ai-tab-hot-${l.id}`}
                                    light={light}
                                    icon={<UserIcon size={16} />}
                                    label={l.name}
                                    sub={l.property_title ? `Immobile: ${l.property_title}` : null}
                                    onClick={() => setOpenLead(l.id)}
                                    pills={[l.priority === "alta"
                                        ? { tone: "red", label: "Alta priorità" }
                                        : { tone: "blue", label: l.stage || "Attivo" }]}
                                />
                            ))}
                        </SectionCard>
                    )}

                    {/* MATCH AUTOMATICI */}
                    {matchAlerts.length > 0 && (
                        <SectionCard
                            testid="ai-matches"
                            light={light}
                            icon={<Target size={16} />}
                            iconClass={light ? "text-purple-600" : "text-[#C084FC]"}
                            title={`Match clienti ↔ immobili (${matchAlerts.length})`}
                            subtitle="Nuovi immobili compatibili con i tuoi clienti"
                        >
                            {matchAlerts.slice(0, 6).map((m) => (
                                <Row
                                    key={`${m.lead_id}-${m.property_title}`}
                                    testid={`ai-tab-match-${m.lead_id}`}
                                    light={light}
                                    icon={<UserIcon size={16} />}
                                    label={m.lead_name}
                                    sub={m.property_title}
                                    onClick={() => setOpenLead(m.lead_id)}
                                    pills={[{ tone: m.score >= 80 ? "green" : "amber", label: `${m.score}%` }]}
                                />
                            ))}
                        </SectionCard>
                    )}

                    {/* INCARICHI IN SCADENZA */}
                    <SectionCard
                        testid="ai-incarichi"
                        light={light}
                        icon={<Clock size={16} />}
                        iconClass={light ? "text-amber-600" : "text-[#FBBF24]"}
                        title={`Incarichi in scadenza (${incarichi.length})`}
                        subtitle="Incarichi che scadono nei prossimi 60 giorni"
                    >
                        {incarichi.length === 0 && (
                            <div className={`text-[13px] py-3 px-2 ${light ? "text-slate-500" : "text-ink-500"}`}>Nessun incarico in scadenza.</div>
                        )}
                        {incarichi.slice(0, 6).map((i) => {
                            const dl = deadlinePill(i.days_left);
                            return (
                                <Row
                                    key={i.lead_id}
                                    testid={`ai-tab-incarico-${i.lead_id}`}
                                    light={light}
                                    icon={<HomeIcon size={16} />}
                                    label={i.name}
                                    sub={i.exclusive ? "Esclusiva" : null}
                                    onClick={() => setOpenLead(i.lead_id)}
                                    pills={[{ tone: dl.tone, label: dl.label }]}
                                />
                            );
                        })}
                    </SectionCard>

                    {/* TRATTATIVE FERME */}
                    {trattFerme.length > 0 && (
                        <SectionCard
                            testid="ai-ferme"
                            light={light}
                            icon={<PauseCircle size={16} />}
                            iconClass={light ? "text-red-600" : "text-[#FF6B6B]"}
                            title={`Trattative ferme (${trattFerme.length})`}
                            subtitle="Ferme da oltre 14 giorni"
                        >
                            {trattFerme.slice(0, 6).map((l) => (
                                <Row
                                    key={l.id}
                                    testid={`ai-tab-ferma-${l.id}`}
                                    light={light}
                                    icon={<UserIcon size={16} />}
                                    label={l.name}
                                    sub={l.agent ? `Agente: ${l.agent}` : null}
                                    onClick={() => setOpenLead(l.id)}
                                    pills={[{ tone: "red", label: `${l.days_inactive} giorni` }]}
                                />
                            ))}
                        </SectionCard>
                    )}

                    {/* DOCUMENTAZIONE INCOMPLETA */}
                    {docIncomplete.length > 0 && (
                        <SectionCard
                            testid="ai-documentazione"
                            light={light}
                            icon={<FileWarning size={16} />}
                            iconClass={light ? "text-amber-600" : "text-[#FBBF24]"}
                            title={`Documentazione da completare (${docIncomplete.length})`}
                            subtitle={ins.documentazione.media_completezza != null ? `Media portafoglio: ${ins.documentazione.media_completezza}%` : null}
                        >
                            {docIncomplete.slice(0, 6).map((d) => (
                                <Link
                                    key={d.property_id}
                                    to={`/annunci/${d.property_id}/modifica`}
                                    className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors group ${light ? "hover:bg-slate-100" : "hover:bg-white/5"}`}
                                >
                                    <HomeIcon size={16} className={`shrink-0 ${light ? "text-[#1E40AF]" : "text-[#5FA7FF]"}`} />
                                    <span className="flex-1 min-w-0">
                                        <span className={`block text-[15px] truncate ${light ? "text-slate-900 group-hover:text-[#1E40AF]" : "text-white group-hover:text-[#5FA7FF]"}`}>{d.title}</span>
                                        {d.missing?.length > 0 && (
                                            <span className={`block text-[12px] truncate mt-0.5 ${light ? "text-slate-500" : "text-ink-400"}`}>Manca: {d.missing.join(", ")}</span>
                                        )}
                                    </span>
                                    <Pill light={light} tone={d.pct >= 80 ? "green" : d.pct >= 50 ? "amber" : "red"}>{d.pct}%</Pill>
                                </Link>
                            ))}
                        </SectionCard>
                    )}

                    {/* ATTIVITÀ CONSIGLIATE */}
                    {suggestedActions.length > 0 && (
                        <SectionCard
                            testid="ai-attivita"
                            light={light}
                            icon={<ClipboardList size={16} />}
                            iconClass={light ? "text-purple-600" : "text-[#C084FC]"}
                            title="Attività consigliate"
                            subtitle="Azioni suggerite per oggi"
                        >
                            {suggestedActions.map((label, i) => (
                                <div key={i} className={`flex items-center gap-3 px-2 py-1.5 text-[15px] ${light ? "text-slate-900" : "text-white"}`}>
                                    <CheckCircle2 size={16} className={`shrink-0 ${light ? "text-[#1E40AF]" : "text-[#5FA7FF]"}`} />
                                    <span>{label}</span>
                                </div>
                            ))}
                        </SectionCard>
                    )}
                </div>
            </div>

            {/* -------- Right column: chat + advanced settings -------- */}
            <div className="lg:col-span-2 space-y-4">
                {/* Chat AI */}
                <div
                    className="rounded-xl overflow-hidden flex flex-col"
                    style={{ backgroundColor: light ? "#FFFFFF" : "#111111", minHeight: 520, border: light ? "1px solid #E2E8F0" : "none" }}
                >
                    <div className={`px-5 py-4 flex items-center gap-3 border-b ${light ? "border-slate-200" : "border-white/8"}`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${light ? "bg-blue-50 border border-blue-200" : "bg-[#0F1E38] border border-[#007BFF]/40"}`}>
                            <Sparkles size={16} className={light ? "text-[#1E40AF]" : "text-[#5FA7FF]"} />
                        </div>
                        <div>
                            <h4 className={`text-[14px] font-semibold leading-tight ${light ? "text-slate-900" : "text-white"}`}>Assistant chat</h4>
                            <p className={`text-[11px] leading-tight mt-0.5 ${light ? "text-slate-500" : "text-ink-400"}`}>Chiedi qualunque cosa sui tuoi lead</p>
                        </div>
                    </div>

                    <div data-testid="ai-conversation" className="flex-1 px-5 py-4 overflow-y-auto" style={{ maxHeight: 500 }}>
                        {conversation.length === 0 && (
                            <div className={`text-[13px] leading-relaxed ${light ? "text-slate-600" : "text-ink-400"}`}>
                                Interroga in tempo reale i dati del tuo CRM (lead, agenti, task, visite) ed esegui azioni:
                                <br /><span className={`italic ${light ? "text-slate-400" : "text-ink-500"}`}>«Quali lead segue Alice?», «Assegna Mario a Luca», «Crea una task di follow-up per Mario entro venerdì»</span>
                            </div>
                        )}
                        {conversation.map((m, i) => (
                            <div key={`${i}-${m.role}`} className={`my-2 ${m.role === "user" ? "flex justify-end" : "flex justify-start"}`}>
                                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-[#007BFF] text-white"
                                        : light ? "bg-slate-100 text-slate-800" : "bg-white/5 text-ink-100"
                                }`}>
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {conversation.flatMap((m, i) => (m.drafts || []).map((d) => (
                            <EmailDraftCard key={`${i}-${d.id}`} draft={d} light={light} />
                        )))}
                        {asking && <div className={`text-[12px] animate-pulse ml-2 mt-2 ${light ? "text-slate-500" : "text-ink-400"}`}>Sto analizzando…</div>}
                        <div ref={chatEndRef} />
                    </div>

                    <div className="px-4 pb-4 pt-2">
                        {voiceUnsupported && (
                            <div className={`text-[11px] mb-2 px-2 ${light ? "text-slate-500" : "text-ink-400"}`}>
                                Il tuo browser non supporta il riconoscimento vocale. Prova Chrome, Edge o Safari.
                            </div>
                        )}
                        <div className={`flex items-center gap-2 px-2 py-2 rounded-full border transition-colors ${
                            listening
                                ? "border-[#FF4D4D]/60"
                                : light ? "border-slate-300 focus-within:border-[#007BFF]/60" : "border-white/10 focus-within:border-[#007BFF]/60"
                        } ${light ? "bg-slate-50" : "bg-[#1A1A1A]"}`}>
                            <button
                                data-testid="ai-tab-mic"
                                onClick={toggleVoice}
                                aria-label={listening ? "Ferma registrazione" : "Parla con l'assistente"}
                                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                    listening
                                        ? "bg-[#FF4D4D] text-white animate-pulse"
                                        : light ? "text-slate-600 hover:bg-slate-200" : "text-ink-300 hover:bg-white/10"
                                }`}
                            >
                                {listening ? <MicOff size={15} strokeWidth={2.4} /> : <Mic size={15} strokeWidth={2.4} />}
                            </button>
                            <input
                                data-testid="ai-question-input"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && ask()}
                                placeholder={listening ? "Ti sto ascoltando…" : "Es. Quanti lead segue ogni agente?"}
                                className={`flex-1 bg-transparent text-[14px] outline-none py-1.5 ${light ? "text-slate-900 placeholder:text-slate-400" : "text-white placeholder:text-ink-500"}`}
                            />
                            <button
                                data-testid="ai-ask"
                                onClick={ask}
                                disabled={asking || !question.trim()}
                                aria-label="Invia domanda"
                                className="w-9 h-9 rounded-full flex items-center justify-center bg-[#007BFF] text-white shrink-0 disabled:opacity-40 hover:bg-[#0069DA] transition-colors"
                            >
                                <Send size={15} strokeWidth={2.5} className="-translate-x-[1px]" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Advanced: briefing settings + settings panel + email log */}
                <CollapsibleCard title="Briefing giornaliero" icon={<Mail size={15} />} testid="briefing-settings" light={light}>
                    <p className={`text-[13px] mb-3 leading-relaxed ${light ? "text-slate-700" : "text-ink-300"}`}>
                        Ogni mattina ricevi via email i lead da seguire, le trattative ferme e le visite di oggi.
                        {briefing?.last_sent && <span className={`block mt-1 ${light ? "text-slate-500" : "text-ink-500"}`}>Ultimo invio: {briefing.last_sent}</span>}
                    </p>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <button
                            data-testid="briefing-toggle"
                            onClick={toggleBriefing}
                            role="switch"
                            aria-checked={!!briefing?.enabled}
                            className={`relative w-11 h-6 rounded-full transition-colors ${briefing?.enabled ? "bg-[#007BFF]" : light ? "bg-slate-300" : "bg-ink-700"}`}
                        >
                            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${briefing?.enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                        </button>
                        <button
                            data-testid="briefing-send-now"
                            onClick={sendBriefingNow}
                            disabled={sendingNow}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] transition-colors disabled:opacity-40 ${light ? "text-slate-700 border border-slate-300 hover:border-slate-500 hover:bg-slate-50" : "text-ink-200 border border-white/15 hover:border-white/40 hover:bg-white/5"}`}
                        >
                            {sendingNow ? "Invio…" : "Inviami il briefing ora"}
                        </button>
                    </div>
                </CollapsibleCard>

                <CollapsibleCard title="Impostazioni AI" icon={<Sparkles size={15} />} testid="ai-settings-panel" light={light}>
                    <AiAssistantSettings />
                </CollapsibleCard>

                <AgentEmailLog light={light} />
            </div>

            {openLead && (
                <LeadDrawer
                    leadId={openLead}
                    agents={agents}
                    onClose={() => setOpenLead(null)}
                    onChanged={load}
                />
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*                              Sub-components                                */
/* -------------------------------------------------------------------------- */

/** Collapsible card container styled to match the AI palette. */
function CollapsibleCard({ title, icon, children, testid, defaultOpen = false, light = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const bg = light ? "#FFFFFF" : "#111111";
    const borderCls = light ? "border-slate-200" : "border-white/8";
    const iconCls = light ? "text-[#1E40AF]" : "text-[#5FA7FF]";
    const titleCls = light ? "text-slate-900" : "text-white";
    const chevronCls = light ? "text-slate-500" : "text-ink-400";
    const hoverCls = light ? "hover:bg-slate-50" : "hover:bg-white/5";
    return (
        <div data-testid={testid} className={`rounded-xl border ${borderCls} overflow-hidden`} style={{ backgroundColor: bg }}>
            <button
                onClick={() => setOpen(!open)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${hoverCls}`}
            >
                <span className="flex items-center gap-2.5">
                    <span className={iconCls}>{icon}</span>
                    <span className={`text-[13px] uppercase tracking-[0.14em] font-semibold ${titleCls}`}>{title}</span>
                </span>
                {open ? <ChevronUp size={16} className={chevronCls} /> : <ChevronDown size={16} className={chevronCls} />}
            </button>
            {open && <div className={`px-5 pb-5 pt-2 border-t ${borderCls}`}>{children}</div>}
        </div>
    );
}

/** Editable email draft that the AI proposes for an agent contact. */
function EmailDraftCard({ draft, light = false }) {
    const [subject, setSubject] = useState(draft.subject);
    const [body, setBody] = useState(draft.body);
    const [status, setStatus] = useState(draft.status);
    const [busy, setBusy] = useState(false);

    const send = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/crm/agent-emails/${draft.id}/send`, { subject, body });
            setStatus(r.data.status);
        } catch { setStatus("failed"); }
        finally { setBusy(false); }
    };
    const discard = async () => {
        try { await api.post(`/crm/agent-emails/${draft.id}/discard`); } catch (e) { console.error(e); }
        setStatus("discarded");
    };

    if (status === "sent") {
        return (
            <div data-testid={`draft-sent-${draft.id}`} className={`my-2 p-3 rounded-lg text-[13px] ${light ? "bg-green-50 border border-green-200 text-green-700" : "bg-[#0E2617] border border-[#22C55E]/40 text-[#4ADE80]"}`}>
                ✅ Email inviata a {draft.agent} ({draft.agent_email})
            </div>
        );
    }
    if (status === "failed") {
        return (
            <div data-testid={`draft-failed-${draft.id}`} className={`my-2 p-3 rounded-lg text-[13px] ${light ? "bg-red-50 border border-red-200 text-red-700" : "bg-[#3B1414] border border-[#FF4D4D]/40 text-[#FF6B6B]"}`}>
                ❌ Invio fallito a {draft.agent_email}. Verifica l&apos;indirizzo o riprova dallo storico email.
            </div>
        );
    }
    if (status === "discarded") {
        return (
            <div className={`my-2 p-3 rounded-lg text-[13px] ${light ? "border border-slate-200 text-slate-500" : "border border-white/10 text-ink-500"}`}>
                Bozza scartata.
            </div>
        );
    }
    const inputCls = light
        ? "w-full mb-2 bg-white border border-slate-300 px-3 py-2 rounded-lg text-[13px] text-slate-900 focus:border-[#007BFF]/60 outline-none"
        : "w-full mb-2 bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-[13px] text-white focus:border-[#007BFF]/60 outline-none";
    const textareaCls = light
        ? "w-full bg-white border border-slate-300 px-3 py-2 rounded-lg text-[13px] text-slate-800 focus:border-[#007BFF]/60 outline-none mb-3"
        : "w-full bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-[13px] text-ink-100 focus:border-[#007BFF]/60 outline-none mb-3";
    return (
        <div data-testid={`email-draft-${draft.id}`} className={`my-3 p-4 rounded-lg ${light ? "bg-slate-50 border border-slate-200" : "bg-[#1A1A1A] border border-white/10"}`}>
            <div className={`text-[11px] uppercase tracking-[0.14em] font-semibold mb-3 flex items-center gap-2 ${light ? "text-[#1E40AF]" : "text-[#5FA7FF]"}`}>
                <Mail size={13} /> Bozza email per {draft.agent} · {draft.agent_email}
            </div>
            <input data-testid="draft-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
            <textarea data-testid="draft-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} className={textareaCls} />
            <div className="flex gap-2">
                <button
                    data-testid="draft-send-btn"
                    onClick={send}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-medium bg-[#007BFF] text-white hover:bg-[#0069DA] disabled:opacity-40 transition-colors"
                >
                    {busy ? "Invio…" : "Invia email"}
                </button>
                <button
                    data-testid="draft-discard-btn"
                    onClick={discard}
                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] transition-colors ${light ? "text-slate-700 border border-slate-300 hover:border-slate-500 hover:bg-slate-100" : "text-ink-200 border border-white/15 hover:border-white/40 hover:bg-white/5"}`}
                >
                    Scarta
                </button>
            </div>
        </div>
    );
}

/** History log of AI-generated agent emails. */
function AgentEmailLog({ light = false }) {
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const load = () => api.get("/crm/agent-emails").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    const bg = light ? "#FFFFFF" : "#111111";
    const borderCls = light ? "border-slate-200" : "border-white/8";
    const iconCls = light ? "text-[#1E40AF]" : "text-[#5FA7FF]";
    const titleCls = light ? "text-slate-900" : "text-white";
    const chevronCls = light ? "text-slate-500" : "text-ink-400";
    const rowBorderCls = light ? "border-slate-200" : "border-white/5";
    const hoverCls = light ? "hover:bg-slate-50" : "hover:bg-white/5";
    return (
        <div data-testid="agent-email-log" className={`rounded-xl border ${borderCls} overflow-hidden`} style={{ backgroundColor: bg }}>
            <button
                data-testid="agent-email-log-toggle"
                onClick={() => { setOpen(!open); if (!open) load(); }}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${hoverCls}`}
            >
                <span className="flex items-center gap-2.5">
                    <Mail size={15} className={iconCls} />
                    <span className={`text-[13px] uppercase tracking-[0.14em] font-semibold ${titleCls}`}>Storico email AI</span>
                </span>
                {open ? <ChevronUp size={16} className={chevronCls} /> : <ChevronDown size={16} className={chevronCls} />}
            </button>
            {open && (
                <div className={`px-4 pb-4 pt-2 border-t ${borderCls} space-y-1.5`}>
                    {items === null && <div className={`text-[13px] py-2 ${light ? "text-slate-500" : "text-ink-500"}`}>Caricamento…</div>}
                    {items?.length === 0 && <div className={`text-[13px] py-2 ${light ? "text-slate-500" : "text-ink-500"}`}>Nessuna email generata dall&apos;AI finora.</div>}
                    {(items || []).map((e) => (
                        <div key={e.id} className={`flex items-center justify-between gap-3 py-2 border-b ${rowBorderCls}`}>
                            <div className="min-w-0">
                                <div className={`text-[13px] truncate ${light ? "text-slate-900" : "text-white"}`}>{e.subject}</div>
                                <div className={`text-[11px] truncate ${light ? "text-slate-500" : "text-ink-500"}`}>{e.agent}</div>
                            </div>
                            <Pill light={light} tone={e.status === "sent" ? "green" : e.status === "failed" ? "red" : e.status === "draft" ? "amber" : "gray"}>
                                {({ sent: "Inviata", failed: "Fallita", draft: "Bozza", discarded: "Scartata" }[e.status]) || e.status}
                            </Pill>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
